import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useMotionValue } from "framer-motion";

/**
 * Default: scroll scrubs the hero video frame by frame.
 * Autoplay: the video plays on its own in a loop; turning it off
 * hands control back to the scroll.
 */
export function useScrollVideo(sectionRef: RefObject<HTMLElement>, videoRef: RefObject<HTMLVideoElement>) {
  const progress = useMotionValue(0);
  const [autoplay, setAutoplay] = useState(false);
  const autoplayRef = useRef(false);

  useEffect(() => {
    let raf = 0;
    let current = 0;

    const loop = () => {
      const section = sectionRef.current;
      const video = videoRef.current;
      if (section) {
        const rect = section.getBoundingClientRect();
        const travel = rect.height - window.innerHeight;
        const p = travel > 0 ? Math.min(1, Math.max(0, -rect.top / travel)) : 0;

        if (video && autoplayRef.current && video.duration) {
          // Progress bar follows playback while autoplaying.
          progress.set(video.currentTime / video.duration);
          current = video.currentTime;
        } else {
          progress.set(p);
          if (video && video.duration && rect.bottom > 0) {
            const target = p * (video.duration - 0.05);
            current += (target - current) * 0.2;
            if (Math.abs(video.currentTime - current) > 0.016 && !video.seeking) {
              video.currentTime = current;
            }
          }
        }
      }
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [sectionRef, videoRef, progress]);

  // iOS only allows seeking after the video has been "played" once.
  const onLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video
      .play()
      .then(() => {
        if (!autoplayRef.current) video.pause();
      })
      .catch(() => undefined);
  }, [videoRef]);

  const stopAutoplay = useCallback(() => {
    const video = videoRef.current;
    autoplayRef.current = false;
    setAutoplay(false);
    if (video) {
      video.loop = false;
      video.pause();
    }
  }, [videoRef]);

  const toggleAutoplay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (autoplayRef.current) {
      stopAutoplay();
      return;
    }
    autoplayRef.current = true;
    setAutoplay(true);
    video.loop = true;
    video.muted = true;
    video.play().catch(stopAutoplay);
  }, [videoRef, stopAutoplay]);

  return { progress, autoplay, toggleAutoplay, onLoadedMetadata };
}
